/**
 * Skeleton atoms for the onboarding app-preview mockup.
 *
 * These are purely decorative — no data, no interactivity. They reproduce the
 * low-detail "product screenshot" look from Figma (node 10154:452) using simple
 * rounded rects so the preview stays crisp at any camera zoom level.
 */

import type { CSSProperties } from "react";

type BarProps = {
  w?: number | string;
  h?: number;
  /** white-on-dark opacity step, matches the Figma skeleton greys */
  shade?: "faint" | "soft" | "mid" | "strong";
  className?: string;
  style?: CSSProperties;
};

const SHADE: Record<NonNullable<BarProps["shade"]>, string> = {
  faint: "rgb(255 255 255 / 0.05)",
  soft: "rgb(255 255 255 / 0.08)",
  mid: "rgb(255 255 255 / 0.12)",
  strong: "rgb(255 255 255 / 0.18)",
};

export function Bar({ w = 60, h = 8, shade = "soft", className = "", style }: BarProps) {
  return (
    <div
      className={`rounded-full ${className}`}
      style={{
        width: typeof w === "number" ? `${w}px` : w,
        height: `${h}px`,
        background: SHADE[shade],
        ...style,
      }}
    />
  );
}

export function Dot({ size = 8, shade = "mid", className = "" }: { size?: number; shade?: BarProps["shade"]; className?: string }) {
  return (
    <div
      className={`shrink-0 rounded-full ${className}`}
      style={{ width: size, height: size, background: SHADE[shade ?? "mid"] }}
    />
  );
}

/** A square icon placeholder (rounded), like the nav/section icons. */
export function IconBox({ size = 18, shade = "soft", className = "" }: { size?: number; shade?: BarProps["shade"]; className?: string }) {
  return (
    <div
      className={`shrink-0 rounded-md ${className}`}
      style={{ width: size, height: size, background: SHADE[shade ?? "soft"] }}
    />
  );
}

/** A pill / chip with a fixed colour (status tags, badges). */
export function Pill({
  w = 40,
  h = 16,
  color,
  className = "",
}: {
  w?: number;
  h?: number;
  color?: string;
  className?: string;
}) {
  return (
    <div
      className={`rounded-md ${className}`}
      style={{ width: w, height: h, background: color ?? SHADE.soft }}
    />
  );
}
